import { SystemUpdates } from "../components/SystemUpdates";
import { useCallback, useEffect, useState } from "react";
import { Eye, RefreshCw, ListChecks, Plus, UserPlus, User, Shield, ScrollText, Settings2, Share2, Trash2, Save } from "lucide-react";
import { api } from "../api";
import { PageHead, Panel, LoadingBlock } from "../shell";
import { BusinessClassificationRulesSettings } from "../components/BusinessClassificationRulesSettings";
import { RoleManagement } from "../components/RoleManagement";
import { PolicyGroupRulesSettings } from "../components/PolicyGroupRulesSettings";
import { can } from "../lib/permissions";

const ROLE_OPTIONS_TR = {
  admin: "Yönetici (Admin)", manager: "Yönetici",
  regional_manager: "Bölge Yöneticisi", seller: "Satıcı", read_only: "Salt Okunur",
};

export default function Settings({ user, onUserRefresh, onImpersonate }) {
  const [tab, setTab] = useState("profile");
  return (
    <main className="content">
      <PageHead eyebrow="AYARLAR" title="Sistem Ayarları" desc="Profil, kullanıcı ve rol yönetimi, denetim günlüğü ve genel ayarlar." />
      <div className="filters" style={{ gap: 8 }}>
        <button data-testid="settings-tab-profile" className={tab === "profile" ? "selected" : ""} onClick={() => setTab("profile")}>
          <User size={13} style={{ marginRight: 4 }}/> Profil
        </button>
        {can(user, "users.manage") && <button data-testid="settings-tab-users" className={tab === "users" ? "selected" : ""} onClick={() => setTab("users")}>
          <UserPlus size={13} style={{ marginRight: 4 }}/> Kullanıcılar ve Yetkiler
        </button>}
        {can(user, "roles.view") && <button data-testid="settings-tab-roles" className={tab === "roles" ? "selected" : ""} onClick={() => setTab("roles")}>
          <Shield size={13} style={{ marginRight: 4 }}/> Roller
        </button>}
        {can(user, "audit.view") && <button data-testid="settings-tab-audit" className={tab === "audit" ? "selected" : ""} onClick={() => setTab("audit")}>
          <ScrollText size={13} style={{ marginRight: 4 }}/> Denetim Günlüğü
        </button>}
        {(can(user, "renewals.manage") || can(user, "portfolio.manage")) && <button data-testid="settings-tab-reporting" className={tab === "reporting" ? "selected" : ""} onClick={() => setTab("reporting")}>
          <Settings2 size={13} style={{ marginRight: 4 }}/> Rapor Ayarları
        </button>}
        {can(user, "crosssell.manage") && <button data-testid="settings-tab-crosssell-rules" className={tab === "crosssell" ? "selected" : ""} onClick={() => setTab("crosssell")}>
          <Share2 size={13} style={{ marginRight: 4 }}/> Çapraz Satış Kuralları
        </button>}
        {["admin", "manager"].includes(user?.role) && !user?.is_impersonating && <button data-testid="settings-tab-policy-group-rules" className={tab === "policy-groups" ? "selected" : ""} onClick={() => setTab("policy-groups")}>
          <ListChecks size={13} style={{ marginRight: 4 }}/> Poliçe Grubu Kuralları
        </button>}
        {user?.role === "admin" && !user?.is_impersonating && <button data-testid="settings-tab-updates" className={tab === "updates" ? "selected" : ""} onClick={() => setTab("updates")}><RefreshCw size={13} style={{ marginRight: 4 }} aria-hidden="true"/> Sistem Güncellemesi</button>}
        <button data-testid="settings-tab-general" className={tab === "general" ? "selected" : ""} onClick={() => setTab("general")}>
          <Settings2 size={13} style={{ marginRight: 4 }}/> Genel
        </button>
      </div>

      {tab === "updates" && user?.role === "admin" && !user?.is_impersonating && <SystemUpdates/>}
      {tab === "profile" && <ProfileTab user={user}/>}
      {tab === "users" && can(user, "users.manage") && <UsersTab currentUser={user} onImpersonate={onImpersonate}/>} 
      {tab === "roles" && can(user, "roles.view") && <RoleManagement onPermissionsChanged={onUserRefresh} onOpenAudit={can(user, "audit.view") ? () => setTab("audit") : null}/>} 
      {tab === "audit" && can(user, "audit.view") && <AuditTab/>}
      {tab === "reporting" && (can(user, "renewals.manage") || can(user, "portfolio.manage")) && <ReportingSettingsTab/>}
      {tab === "crosssell" && can(user, "crosssell.manage") && <CrossSellRulesTab/>}
      {tab === "policy-groups" && ["admin", "manager"].includes(user?.role) && !user?.is_impersonating && <PolicyGroupRulesSettings/>}
      {tab === "general" && <GeneralTab/>}
    </main>
  );
}

function mappingPayload(type, value, options, label) {
  if (!value) return { policy_group_ids: [], policy_group_names: [], branch_names: [], label };
  if (type === "POLICY_GROUP") {
    const group = (options.policy_groups || []).find((item) => item.id === value);
    return { policy_group_ids: [value], policy_group_names: [group?.name || value], branch_names: [], label: label || group?.name || value };
  }
  return { policy_group_ids: [], policy_group_names: [], branch_names: [value], label: label || value };
}

function CrossSellRulesTab() {
  const [rules, setRules] = useState(null); const [previews, setPreviews] = useState([]); const [options, setOptions] = useState({}); const [scores, setScores] = useState(null); const [feedback, setFeedback] = useState(null);
  const [form, setForm] = useState({ name:"", customer_type:"CORPORATE", required_type:"POLICY_GROUP", required_value:"", target_type:"POLICY_GROUP", target_value:"", exclusion_type:"POLICY_GROUP", exclusion_value:"", priority:70, explanation:"" });
  const load = useCallback(async () => { const [r,p,o,s] = await Promise.all([api.get("/cross-sell/rules"),api.get("/cross-sell/rules/previews"),api.get("/cross-sell/options"),api.get("/cross-sell/score-settings")]); setRules(r.data.items); setPreviews(p.data.items); setOptions(o.data); setScores(s.data); }, []);
  useEffect(() => { load().catch(() => setFeedback({ok:false,msg:"Çapraz satış kuralları yüklenemedi."})); }, [load]);
  const values = (type) => type === "POLICY_GROUP" ? options.policy_groups || [] : (options.branches || []).map((name) => ({id:name,name}));
  const create = async () => { setFeedback(null); try { const required=mappingPayload(form.required_type,form.required_value,options); const target=mappingPayload(form.target_type,form.target_value,options); const exclusion=mappingPayload(form.exclusion_type,form.exclusion_value,options); await api.post("/cross-sell/rules", {name:form.name,customer_type:form.customer_type,required,target,exclusions:form.exclusion_value?[exclusion]:[],priority:Number(form.priority),explanation:form.explanation}); setFeedback({ok:true,msg:"Kural DRAFT / INACTIVE olarak oluşturuldu. Aktivasyon için mapping onayı gerekir."}); setForm({...form,name:"",required_value:"",target_value:"",exclusion_value:"",explanation:""}); await load(); } catch(e) { setFeedback({ok:false,msg:e.response?.data?.detail || "Kural oluşturulamadı."}); } };
  const saveScores = async () => { try { await api.put("/cross-sell/score-settings", scores); setFeedback({ok:true,msg:"Deterministik skor ağırlıkları kaydedildi."}); } catch(e) { setFeedback({ok:false,msg:e.response?.data?.detail || "Skor ayarları kaydedilemedi."}); } };
  return <><div className="report-settings-path" data-testid="crosssell-rules-settings-path">Ayarlar <span>›</span> Çapraz Satış Kuralları <span>›</span> Production Mapping</div>{feedback && <div data-testid="crosssell-rules-feedback" className={feedback.ok?"success-box":"error"}>{feedback.msg}</div>}
    <Panel title="Yeni DRAFT Kural" sub="Kural, Admin mapping onayı olmadan ACTIVE olamaz ve kesin fırsat üretmez">
      <div className="crosssell-rule-form"><label>Kural Adı<input data-testid="crosssell-rule-name" value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})}/></label><label>Müşteri Tipi<select data-testid="crosssell-rule-customer-type" value={form.customer_type} onChange={(e)=>setForm({...form,customer_type:e.target.value})}><option value="CORPORATE">Kurumsal</option><option value="INDIVIDUAL">Bireysel</option><option value="UNKNOWN">İnceleme</option><option value="ALL">Tümü</option></select></label><label>Öncelik<input data-testid="crosssell-rule-priority" type="number" min="0" max="100" value={form.priority} onChange={(e)=>setForm({...form,priority:e.target.value})}/></label>
        {[['required','Mevcut Ürün'],['target','Önerilecek Ürün'],['exclusion','Opsiyonel Hariç Tutma']].map(([key,label]) => <div className="rule-mapping-input" key={key}><label>{label} Boyutu<select data-testid={`crosssell-${key}-dimension`} value={form[`${key}_type`]} onChange={(e)=>setForm({...form,[`${key}_type`]:e.target.value,[`${key}_value`]:""})}><option value="POLICY_GROUP">Poliçe Grubu</option><option value="BRANCH">Branş Adı</option></select></label><label>{label} Değeri<select data-testid={`crosssell-${key}-value`} value={form[`${key}_value`]} onChange={(e)=>setForm({...form,[`${key}_value`]:e.target.value})}><option value="">Seçin</option>{values(form[`${key}_type`]).map((item)=><option key={item.id} value={item.id}>{item.name}</option>)}</select></label></div>)}
        <label className="full">Açıklama<textarea data-testid="crosssell-rule-explanation" value={form.explanation} onChange={(e)=>setForm({...form,explanation:e.target.value})}/></label><button data-testid="create-crosssell-draft-rule" className="primary-button" onClick={create}><Plus size={14}/>DRAFT Kural Oluştur</button>
      </div>
    </Panel>
    <Panel title="Production Mapping Analizi" sub="Aday sayıları kesin fırsat değildir; tüm başlangıç kuralları pasiftir"><div className="renewal-rules-table-wrap"><table data-testid="crosssell-settings-rules-table"><thead><tr><th>Kural</th><th>Tip</th><th>Kaynak</th><th>Hedef</th><th>Güven</th><th>Aday</th><th>Prim / Komisyon</th><th>Durum</th></tr></thead><tbody>{(rules||[]).map((rule)=>{const p=previews.find((item)=>item.rule_id===rule.id)||{};return <tr key={rule.id}><td><strong>{rule.name}</strong><small>{rule.explanation}</small></td><td>{rule.customer_type}</td><td>{rule.required?.label}<small>{[...(rule.required?.policy_group_names||[]),...(rule.required?.branch_names||[])].join(", ")}</small></td><td>{rule.target?.label}<small>{[...(rule.target?.policy_group_names||[]),...(rule.target?.branch_names||[])].join(", ")||"Mapping yok"}</small></td><td>{rule.mapping_confidence}</td><td>{p.candidate_count||0}<small>{p.corporate_count||0} K / {p.individual_count||0} B</small></td><td>{p.current_portfolio_premium?.toLocaleString("tr-TR")}<small>{p.current_portfolio_commission?.toLocaleString("tr-TR")}</small></td><td><span className="tag">{rule.status}</span></td></tr>})}</tbody></table></div></Panel>
    <Panel title="Öncelik Skoru" sub="Açıklanabilir toplam 100 puan">{scores && <div className="crosssell-score-settings">{Object.entries(scores).map(([key,value])=><label key={key}>{key.replaceAll('_',' ')}<input data-testid={`crosssell-score-${key.replaceAll('_','-')}`} type="number" value={value} onChange={(e)=>setScores({...scores,[key]:Number(e.target.value)})}/></label>)}<button data-testid="save-crosssell-score-settings" className="primary-button" onClick={saveScores}><Save size={14}/>Skor Ayarlarını Kaydet</button></div>}</Panel>
  </>;
}

const RULE_TYPE_LABELS = {
  SELLER: "Satıcı", BRANCH: "Branş", POLICY_GROUP: "Poliçe Grubu", INSURER: "Sigorta Şirketi",
};

function ReportingSettingsTab() {
  const [section, setSection] = useState("renewal");
  return <>
    <div className="report-settings-section-tabs">
      <button data-testid="settings-renewal-rules-tab" className={section === "renewal" ? "active" : ""} onClick={() => setSection("renewal")}>Yenileme Hariç Tutma</button>
      <button data-testid="settings-business-classification-rules-tab" className={section === "classification" ? "active" : ""} onClick={() => setSection("classification")}>Yeni İş / Yenileme Kuralları</button>
      <button data-testid="settings-portfolio-thresholds-tab" className={section === "portfolio" ? "active" : ""} onClick={() => setSection("portfolio")}>Portföy Analizi Eşikleri</button>
    </div>
    {section === "renewal" ? <RenewalReportingSettings/> : section === "classification" ? <BusinessClassificationRulesSettings/> : <PortfolioThresholdSettings/>}
  </>;
}

function RenewalReportingSettings() {
  const [rules, setRules] = useState(null);
  const [options, setOptions] = useState({});
  const [feedback, setFeedback] = useState(null);
  const [form, setForm] = useState({ rule_type: "SELLER", exact_value: "", reason: "", active: true });

  const load = useCallback(async (signal) => {
    const [ruleResponse, optionResponse] = await Promise.all([
      api.get("/renewals/exclusion-rules", { signal }), api.get("/renewals/exclusion-options", { signal }),
    ]);
    setRules(ruleResponse.data.items || []);
    setOptions(optionResponse.data || {});
  }, []);
  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal).catch((error) => {
      if (error?.code !== "ERR_CANCELED") setFeedback({ ok: false, msg: "Hariç tutma kuralları yüklenemedi." });
    });
    return () => controller.abort();
  }, [load]);

  const setType = (ruleType) => setForm({ ...form, rule_type: ruleType, exact_value: "" });
  const createRule = async () => {
    setFeedback(null);
    try {
      await api.post("/renewals/exclusion-rules", form);
      setFeedback({ ok: true, msg: "Exact hariç tutma kuralı eklendi." });
      setForm({ ...form, exact_value: "", reason: "" });
      await load();
    } catch (e) { setFeedback({ ok: false, msg: e.response?.data?.detail || "Kural eklenemedi." }); }
  };
  const toggleRule = async (rule) => {
    setFeedback(null);
    try {
      await api.patch(`/renewals/exclusion-rules/${rule.id}`, { active: !rule.active });
      setFeedback({ ok: true, msg: rule.active ? "Kural pasifleştirildi." : "Kural aktifleştirildi." });
      await load();
    } catch (e) { setFeedback({ ok: false, msg: e.response?.data?.detail || "Kural güncellenemedi." }); }
  };

  return <>
    <div className="report-settings-path" data-testid="report-settings-path">Rapor Ayarları <span>›</span> Yenileme <span>›</span> Hariç Tutma Kuralları</div>
    {feedback && <div data-testid="report-rules-feedback" className={feedback.ok ? "success-box" : "error"}>{feedback.msg}</div>}
    <section className="lower-grid report-rules-grid">
      <Panel title="Yenileme Hariç Tutma Kuralları" sub="Yalnız exact/normalize edilmiş production değerleri kullanılır">
        {!rules ? <LoadingBlock/> : <div className="renewal-rules-table-wrap"><table data-testid="renewal-exclusion-rules-table">
          <thead><tr><th>Tür</th><th>Exact Değer</th><th>Hariç Tutma Nedeni</th><th>Durum</th></tr></thead>
          <tbody>{rules.map((rule) => <tr key={rule.id} data-testid={`renewal-exclusion-rule-${rule.id}`}>
            <td><span className="tag">{RULE_TYPE_LABELS[rule.rule_type] || rule.rule_type}</span></td>
            <td><strong>{rule.exact_value}</strong><small className="rule-normalized-value">{rule.normalized_value}</small></td>
            <td>{rule.reason}</td>
            <td><button data-testid={`renewal-exclusion-toggle-${rule.id}`} className={`rule-status-button ${rule.active ? "active" : ""}`} onClick={() => toggleRule(rule)}>{rule.active ? "Aktif" : "Pasif"}</button></td>
          </tr>)}</tbody>
        </table></div>}
      </Panel>
      <Panel title="Yeni Exact Kural" sub="Production veri sözlüğünden değer seçin">
        <div className="form-grid renewal-rule-form">
          <label>Kural Türü<select data-testid="renewal-rule-type" value={form.rule_type} onChange={(e) => setType(e.target.value)}>
            {Object.entries(RULE_TYPE_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select></label>
          <label>Exact Production Değeri<select data-testid="renewal-rule-exact-value" value={form.exact_value} onChange={(e) => setForm({ ...form, exact_value: e.target.value })}>
            <option value="">Değer seçin</option>
            {(options[form.rule_type] || []).map((value) => <option key={value} value={value}>{value}</option>)}
          </select></label>
          <label>Hariç Tutma Nedeni<input data-testid="renewal-rule-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Örn. Satıcı Kuralı – ..."/></label>
          <label className="inline-checkbox"><input data-testid="renewal-rule-active" type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })}/> Kuralı aktif oluştur</label>
          <button data-testid="renewal-rule-create" className="primary-button" disabled={!form.exact_value || form.reason.trim().length < 3} onClick={createRule}><Plus size={14}/> Exact kural ekle</button>
        </div>
      </Panel>
    </section>
  </>;
}

const THRESHOLD_FIELDS = [
  ["single_insurer_pct", "Tek Şirket Üst Limiti (%)", "input-thresh-single-insurer"],
  ["top5_insurer_pct", "Top 5 Şirket Üst Limiti (%)", "input-thresh-top5-insurer"],
  ["single_policy_group_pct", "Tek Poliçe Grubu Üst Limiti (%)", "input-thresh-single-policy-group"],
  ["single_insurer_in_branch_pct", "Branş İçi Tek Şirket Limiti (%)", "input-thresh-single-insurer-in-branch"],
  ["single_corporate_customer_pct", "Tek Kurumsal Müşteri Limiti (%)", "input-thresh-single-corporate-customer"],
  ["single_seller_in_region_pct", "Bölge İçi Tek Satıcı Limiti (%)", "input-thresh-single-seller-in-region"],
];

function PortfolioThresholdSettings() {
  const [values, setValues] = useState(null);
  const [feedback, setFeedback] = useState(null);
  useEffect(() => { api.get("/portfolio/thresholds").then((response) => setValues(response.data)); }, []);
  const save = async () => {
    setFeedback(null);
    try { const response = await api.put("/portfolio/thresholds", values); setValues(response.data); setFeedback({ ok: true, msg: "Portföy yoğunlaşma eşikleri kaydedildi." }); }
    catch (problem) { setFeedback({ ok: false, msg: problem.response?.data?.detail || "Eşikler kaydedilemedi." }); }
  };
  return <>
    <div className="report-settings-path" data-testid="portfolio-settings-path">Rapor Ayarları <span>›</span> Portföy Analizi <span>›</span> Yoğunlaşma Eşikleri</div>
    {feedback && <div data-testid="portfolio-threshold-feedback" className={feedback.ok ? "success-box" : "error"}>{feedback.msg}</div>}
    <Panel title="Portföy Yoğunlaşma Eşikleri" sub="Eşik aşımı yalnızca uyarı üretir; rapor hesaplamasını değiştirmez">
      {!values ? <LoadingBlock/> : <div className="portfolio-threshold-grid">
        {THRESHOLD_FIELDS.map(([key, label, testid]) => <label key={key}>{label}<input data-testid={testid} type="number" min="0" max="100" step="0.1" value={values[key]} onChange={(event) => setValues({ ...values, [key]: Number(event.target.value) })}/></label>)}
        <button data-testid="save-portfolio-thresholds-btn" className="primary-button" onClick={save}><Save size={14}/> Eşikleri Kaydet</button>
      </div>}
    </Panel>
  </>;
}

function ProfileTab({ user }) {
  return (
    <Panel title="Hesap Bilgileri" sub="Oturum açtığınız kullanıcı">
      {!user ? <LoadingBlock/> : (
        <div className="form-grid readonly" style={{ gridTemplateColumns: "1fr 1fr", maxWidth: 620 }}>
          <div><span className="muted">Ad Soyad</span><strong data-testid="profile-name">{user.name}</strong></div>
          <div><span className="muted">E-posta</span><strong data-testid="profile-email">{user.email}</strong></div>
          <div><span className="muted">Rol</span><strong data-testid="profile-role">{user.role_label || ROLE_OPTIONS_TR[user.role] || user.role}</strong></div>
          <div><span className="muted">Durum</span><strong>{user.active ? "Aktif" : "Pasif"}</strong></div>
        </div>
      )}
    </Panel>
  );
}

function UsersTab({ currentUser, onImpersonate }) {
  const [items, setItems] = useState(null);
  const [roleOptions, setRoleOptions] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [form, setForm] = useState({ email: "", name: "", role: "read_only", password: "", scope: [], all_company_access: false, target_performance_company_view: false, regions_company_view: false });
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const load = () => api.get("/users").then((r) => { setItems(r.data.items); setRoleOptions(r.data.role_options || []); });
  useEffect(() => {
    load();
    api.get("/organization/tree").then((r) => setNodes(r.data.nodes || []));
  }, []);

  const submit = async () => {
    setFeedback(null);
    try {
      await api.post("/users", form);
      setForm({ email: "", name: "", role: "read_only", password: "", scope: [], all_company_access: false, target_performance_company_view: false, regions_company_view: false });
      setFeedback({ ok: true, msg: "Kullanıcı eklendi." });
      load();
    } catch (e) { setFeedback({ ok: false, msg: e.response?.data?.detail || "Eklenemedi" }); }
  };

  const save = async () => {
    setFeedback(null);
    const payload = { name: editing.name, role: editing.role, active: editing.active,
                       scope: editing.scope || [], all_company_access: !!editing.all_company_access,
                       target_performance_company_view: !!editing.target_performance_company_view,
                       regions_company_view: !!editing.regions_company_view };
    if (editing._newPassword) payload.password = editing._newPassword;
    try {
      await api.patch(`/users/${editing.id}`, payload);
      setEditing(null);
      setFeedback({ ok: true, msg: "Kullanıcı güncellendi." });
      load();
    } catch (e) { setFeedback({ ok: false, msg: e.response?.data?.detail || "Güncellenemedi" }); }
  };

  const remove = async () => {
    if (!deleting) return;
    setFeedback(null);
    try {
      await api.delete(`/users/${deleting.id}`);
      if (editing?.id === deleting.id) setEditing(null);
      setFeedback({ ok: true, msg: `${deleting.email} kalıcı olarak silindi.` });
      setDeleting(null);
      await load();
    } catch (e) {
      setFeedback({ ok: false, msg: e.response?.data?.detail || "Kullanıcı silinemedi." });
      setDeleting(null);
    }
  };
  const viewAsUser = async () => {
    if (!viewing) return;
    setFeedback(null);
    try {
      const response = await api.post(`/auth/impersonate/${viewing.id}`);
      onImpersonate(response.data);
    } catch (e) {
      setFeedback({ ok: false, msg: e.response?.data?.detail || "Kullanıcı görünümü başlatılamadı." });
      setViewing(null);
    }
  };

  const toggleScope = (obj, nid) => {
    const s = new Set(obj.scope || []);
    if (s.has(nid)) s.delete(nid); else s.add(nid);
    return Array.from(s);
  };
  const roleBase = (roleId) => roleOptions.find((role) => role.id === roleId)?.base_role || roleId;
  const normalizeName = (value) => String(value || "").toLocaleUpperCase("tr-TR").replace(/\s+/g, " ").trim();
  const defaultSellerScope = (roleId, name, scope) => {
    if (roleBase(roleId) !== "seller" || (scope || []).length) return scope || [];
    const key = normalizeName(name);
    const matches = nodes.filter((node) => node.node_type === "SELLER" && normalizeName(node.node_name) === key);
    return matches.length === 1 ? [matches[0].id] : [];
  };
  const scopeLabel = (record) => record.all_company_access || record.role === "admin" || (record.role === "manager" && !(record.scope || []).length)
    ? "Tüm şirket"
    : record.scope_names?.length ? record.scope_names.join(", ") : "Atanmış kapsam yok";

  return (
    <>
      {feedback && <div className={feedback.ok ? "success-box" : "error"} data-testid="users-feedback">{feedback.msg}</div>}
      <section className="lower-grid user-settings-grid">
        <Panel title="Sistem Kullanıcıları" sub={items ? `${items.length} kullanıcı` : "yükleniyor..."}>
          {!items ? <LoadingBlock/> : (
            <table data-testid="users-table" className="users-table">
              <thead><tr><th>Ad</th><th>E-posta</th><th>Rol</th><th>Kapsam (Organizasyon)</th><th>Son giriş</th><th>Aktif</th><th></th></tr></thead>
              <tbody>{items.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`}>
                  <td data-label="Ad"><strong>{u.name}</strong></td>
                  <td data-label="E-posta">{u.email}</td>
                  <td data-label="Rol"><span className="tag">{u.role_label}</span></td>
                  <td data-label="Kapsam" className="muted" style={{ fontSize: 11 }}>
                    {scopeLabel(u)}
                  </td>
                  <td data-label="Son giriş" className="muted" style={{ fontSize: 11 }}>
                    {u.last_login_at ? new Date(u.last_login_at).toLocaleString("tr-TR") : "Geçmiş kaydı yok"}
                  </td>
                  <td data-label="Aktif">{u.active ? "✓" : "—"}</td>
                  <td data-label="İşlem">
                    {u.active && u.id !== currentUser.id && <button data-testid={`view-as-user-${u.id}`} aria-label={`${u.email} kullanıcısı olarak görüntüle`} title="Kullanıcı olarak görüntüle" className="text-button" onClick={() => setViewing(u)}><Eye size={12}/></button>}
                    <button data-testid={`edit-user-${u.id}`} className="text-button" onClick={() => setEditing({ ...u, role: u.assigned_role_id, scope: defaultSellerScope(u.role, u.name, u.scope) })}>Düzenle</button>{" "}
                    <button data-testid={`delete-user-${u.id}`} aria-label={`${u.email} kullanıcısını sil`} title="Kullanıcıyı kalıcı sil" className="text-button" onClick={() => setDeleting(u)}><Trash2 size={11}/></button>
                  </td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </Panel>
        <Panel title={editing ? "Kullanıcı Düzenle" : "Yeni Kullanıcı Ekle"} sub={editing ? editing.email : "Rol ve kapsam belirleyin"}>
          {editing ? (
            <div className="form-grid">
              <label>Ad<input data-testid="edit-user-name" value={editing.name} onChange={(e) => { const name = e.target.value; setEditing({ ...editing, name, scope: defaultSellerScope(editing.role, name, editing.scope) }); }}/></label>
              <label>Rol<select data-testid="edit-user-role" value={editing.role} onChange={(e) => { const role = e.target.value; setEditing({ ...editing, role, scope: defaultSellerScope(role, editing.name, editing.scope) }); }}>
                {roleOptions.map((role) => <option key={role.id} value={role.id}>{role.label}{role.system ? "" : " · Özel"}</option>)}
              </select></label>
              <label>Yeni Şifre (boş bırakırsanız değişmez)<input data-testid="edit-user-password" type="password" value={editing._newPassword || ""} onChange={(e) => setEditing({ ...editing, _newPassword: e.target.value })}/></label>
              <label><input data-testid="edit-user-active" type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })}/> Aktif</label>
              <label><input data-testid="edit-user-all-company-access" type="checkbox" checked={!!editing.all_company_access} onChange={(e) => setEditing({ ...editing, all_company_access: e.target.checked })}/> Tüm şirketi gör</label>
              <label><input data-testid="edit-user-target-company-view" type="checkbox" checked={!!editing.target_performance_company_view} onChange={(e) => setEditing({ ...editing, target_performance_company_view: e.target.checked })}/> Hedef Performansı → Tüm şirket hedef/gerçekleşenini görüntüleyebilir</label>
              <label><input data-testid="edit-user-regions-company-view" type="checkbox" checked={!!editing.regions_company_view} onChange={(e) => setEditing({ ...editing, regions_company_view: e.target.checked })}/> Bölgeler → Tüm bölgelerin performansını görüntüleyebilir</label>
              {!editing.all_company_access && <ScopeSelector nodes={nodes} value={editing.scope || []} onChange={(nid) => setEditing({ ...editing, scope: toggleScope(editing, nid) })}/>} 
              <div style={{ display: "flex", gap: 8 }}>
                <button data-testid="edit-user-save" className="primary-button" onClick={save}><Save size={13}/> Kaydet</button>
                <button data-testid="edit-user-cancel" className="outline-button" onClick={() => setEditing(null)}>İptal</button>
              </div>
            </div>
          ) : (
            <div className="form-grid">
              <label>Ad<input data-testid="new-user-name" value={form.name} onChange={(e) => { const name = e.target.value; setForm({ ...form, name, scope: defaultSellerScope(form.role, name, form.scope) }); }}/></label>
              <label>E-posta<input data-testid="new-user-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}/></label>
              <label>Şifre<input data-testid="new-user-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}/></label>
              <label>Rol<select data-testid="new-user-role" value={form.role} onChange={(e) => { const role = e.target.value; setForm({ ...form, role, scope: defaultSellerScope(role, form.name, form.scope) }); }}>
                {roleOptions.map((role) => <option key={role.id} value={role.id}>{role.label}{role.system ? "" : " · Özel"}</option>)}
              </select></label>
              <label><input data-testid="new-user-all-company-access" type="checkbox" checked={!!form.all_company_access} onChange={(e) => setForm({ ...form, all_company_access: e.target.checked })}/> Tüm şirketi gör</label>
              <label><input data-testid="new-user-target-company-view" type="checkbox" checked={!!form.target_performance_company_view} onChange={(e) => setForm({ ...form, target_performance_company_view: e.target.checked })}/> Hedef Performansı → Tüm şirket hedef/gerçekleşenini görüntüleyebilir</label>
              <label><input data-testid="new-user-regions-company-view" type="checkbox" checked={!!form.regions_company_view} onChange={(e) => setForm({ ...form, regions_company_view: e.target.checked })}/> Bölgeler → Tüm bölgelerin performansını görüntüleyebilir</label>
              {!form.all_company_access && <ScopeSelector nodes={nodes} value={form.scope} onChange={(nid) => setForm({ ...form, scope: toggleScope(form, nid) })}/>} 
              <button data-testid="new-user-submit" className="primary-button" onClick={submit}><UserPlus size={14}/> Kullanıcı ekle</button>
            </div>
          )}
        </Panel>
      </section>
      {deleting && <div className="modal-backdrop" data-testid="delete-user-dialog" onClick={() => setDeleting(null)}>
        <div className="modal role-dialog delete-user-modal" onClick={(event) => event.stopPropagation()}>
          <div className="modal-head"><div><span className="eyebrow">KULLANICIYI SİL</span><strong data-testid="delete-user-email">{deleting.email}</strong></div></div>
          <p>Bu kullanıcı kaydı kalıcı olarak silinecek. Aktif/pasif yönetimi için düzenleme ekranını kullanabilirsiniz.</p>
          <div className="role-dialog-actions">
            <button data-testid="confirm-delete-user-button" className="danger-button" onClick={remove}><Trash2 size={13}/>Kalıcı Sil</button>
            <button data-testid="cancel-delete-user-button" className="outline-button" onClick={() => setDeleting(null)}>Vazgeç</button>
          </div>
        </div>
      </div>}
      {viewing && <div className="modal-backdrop" data-testid="view-as-user-dialog" onClick={() => setViewing(null)}>
        <div className="modal role-dialog view-as-user-modal" onClick={(event) => event.stopPropagation()}>
          <div className="modal-head"><div><span className="eyebrow">KULLANICI GÖRÜNÜMÜ</span><strong data-testid="view-as-user-email">{viewing.email}</strong></div></div>
          <p>Menüler ve veriler bu kullanıcının gerçek rolü ve organizasyon kapsamıyla açılır. Bu oturumda hiçbir kayıt değiştirilemez.</p>
          <div className="view-as-user-facts" data-testid="view-as-user-facts">
            <div><span>Rol</span><strong data-testid="view-as-user-role">{viewing.role_label}</strong></div>
            <div><span>Organizasyon Kapsamı</span><strong data-testid="view-as-user-scope">{scopeLabel(viewing)}</strong></div>
          </div>
          <div className="role-dialog-actions">
            <button data-testid="confirm-view-as-user-button" className="primary-button" onClick={viewAsUser}><Eye size={13}/>Salt Okunur Görüntüle</button>
            <button data-testid="cancel-view-as-user-button" className="outline-button" onClick={() => setViewing(null)}>Vazgeç</button>
          </div>
        </div>
      </div>}
    </>
  );
}

function ScopeSelector({ nodes, value, onChange }) {
  const [q, setQ] = useState("");
  const filtered = nodes.filter((n) => !q || (n.path || n.node_name).toUpperCase().includes(q.toUpperCase())).slice(0, 200);
  const isChecked = (id) => value.includes(id);
  return (
    <div>
      <label style={{ marginBottom: 4 }}>Organizasyon Kapsamı<span className="muted" style={{ fontSize: 10, marginLeft: 6 }}>(bölge, ekip veya satıcı seçin)</span></label>
      <input data-testid="scope-search" placeholder="Bölge/Ekip/Satıcı ara" value={q} onChange={(e) => setQ(e.target.value)} style={{ padding: 7, width: "100%", marginBottom: 6 }}/>
      <div style={{ maxHeight: 220, overflow: "auto", border: "1px solid var(--line)", borderRadius: 3, padding: 6, background: "#fff" }} data-testid="scope-list">
        {filtered.map((n) => (
          <label key={n.id} style={{ display: "flex", gap: 6, fontSize: 11, padding: "3px 4px", cursor: "pointer" }}>
            <input data-testid={`scope-node-${n.id}`} type="checkbox" checked={isChecked(n.id)} onChange={() => onChange(n.id)}/>
            <span className="tag" style={{ fontSize: 9 }}>{n.node_type}</span>
            <span>{n.path || n.node_name}</span>
          </label>
        ))}
        {filtered.length === 0 && <div className="muted" style={{ fontSize: 11 }}>Sonuç yok.</div>}
      </div>
      {value.length > 0 && <div className="muted" style={{ fontSize: 10, marginTop: 4 }}>{value.length} düğüm seçili</div>}
    </div>
  );
}

function AuditTab() {
  const [audit, setAudit] = useState(null);
  useEffect(() => { api.get("/audit-log").then((r) => setAudit(r.data)).catch(() => setAudit({ items: [] })); }, []);
  return (
    <Panel title="Denetim Günlüğü" sub="Son 100 işlem">
      {!audit ? <LoadingBlock/> : (
        <table data-testid="audit-log-table">
          <thead><tr><th>Tarih</th><th>Kullanıcı</th><th>Aksiyon</th><th>Kaynak</th></tr></thead>
          <tbody>{audit.items.map((it, i) => (
            <tr key={i}><td>{new Date(it.created_at).toLocaleString("tr-TR")}</td>
              <td>{it.user_email}</td><td><span className="tag">{it.action}</span></td>
              <td className="muted">{it.resource || "—"}</td></tr>
          ))}
          {audit.items.length === 0 && <tr><td colSpan={4} className="empty-state">Henüz kayıt yok.</td></tr>}
          </tbody>
        </table>
      )}
    </Panel>
  );
}

function GeneralTab() {
  return (
    <Panel title="ADA Yazılım Entegrasyonu" sub="Windows Sync Service ile ADA verisi çekmek için hazır">
      <div className="totals-strip">
        <div><span>Durum</span><strong>Pasif</strong></div>
        <div><span>Endpoint</span><strong>/api/import/ada</strong></div>
        <div><span>Son senkronizasyon</span><strong>—</strong></div>
      </div>
      <p className="muted" style={{ marginTop: 12, fontSize: 12 }}>
        Ofis içindeki Windows Sync Service ADA API'sinden veri alıp <code>X-ADA-TOKEN</code> başlığıyla bu endpoint'e gönderecek.
        Token değerini production'da <code>ADA_SYNC_TOKEN</code> ortam değişkeni ile ayarlayın.
      </p>
    </Panel>
  );
}
